import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecipes } from '@/hooks/useRecipes';
import type { MenuTier, CreateMenuRequest, Recipe } from '@/types/api';

export interface MenuEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<CreateMenuRequest, 'date' | 'mealType' | 'createdBy'>) => void;
  title: string;
}

const TIER_OPTIONS: { value: MenuTier; labelKey: string; descKey: string; color: string }[] = [
  { value: 'recipe', labelKey: 'menu.tierRecipe', descKey: 'menu.tierRecipeDesc', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'semi', labelKey: 'menu.tierSemi', descKey: 'menu.tierSemiDesc', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'cheat', labelKey: 'menu.tierCheat', descKey: 'menu.tierCheatDesc', color: 'bg-red-100 text-red-800 border-red-300' },
];

export function MenuEntryForm({ isOpen, onClose, onSubmit, title }: MenuEntryFormProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [tier, setTier] = useState<MenuTier>('cheat');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [label, setLabel] = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');
  const [energyKcal, setEnergyKcal] = useState('');
  const [proteins, setProteins] = useState('');
  const [carbohydrates, setCarbohydrates] = useState('');
  const [fat, setFat] = useState('');
  const [fiber, setFiber] = useState('');

  const { data: recipesData, isLoading: recipesLoading } = useRecipes({
    query: recipeSearch || undefined,
    limit: 20,
  });

  useEffect(() => {
    if (!isOpen) {
      setTier('cheat');
      setSelectedRecipe(null);
      setLabel('');
      setRecipeSearch('');
      setEnergyKcal('');
      setProteins('');
      setCarbohydrates('');
      setFat('');
      setFiber('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit =
    (tier === 'recipe' && selectedRecipe != null) ||
    (tier === 'semi' && label.trim().length > 0) ||
    (tier === 'cheat' && label.trim().length > 0);

  const handleSubmit = () => {
    if (!canSubmit) return;

    const data: Omit<CreateMenuRequest, 'date' | 'mealType' | 'createdBy'> = { tier };

    if (tier === 'recipe' && selectedRecipe) {
      data.recipeId = selectedRecipe.id;
    } else {
      data.label = label.trim();
    }

    if (tier === 'semi') {
      if (energyKcal) data.energyKcal = Number(energyKcal);
      if (proteins) data.proteins = Number(proteins);
      if (carbohydrates) data.carbohydrates = Number(carbohydrates);
      if (fat) data.fat = Number(fat);
      if (fiber) data.fiber = Number(fiber);
    }

    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Tier selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('menu.selectTier')}</label>
            <div className="grid grid-cols-3 gap-2">
              {TIER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setTier(opt.value); setSelectedRecipe(null); setLabel(''); }}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    tier === opt.value
                      ? `${opt.color} border-current`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">{t(opt.labelKey)}</div>
                  <div className="text-[11px] mt-0.5 opacity-75">{t(opt.descKey)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Recipe tier: recipe picker */}
          {tier === 'recipe' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('menu.selectRecipe')}</label>
              <input
                type="text"
                placeholder={t('menu.searchRecipes')}
                value={recipeSearch}
                onChange={(e) => setRecipeSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-3"
              />
              {recipesLoading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(recipesData?.recipes ?? []).map((recipe) => (
                    <button
                      key={recipe.id}
                      type="button"
                      onClick={() => setSelectedRecipe(recipe)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        selectedRecipe?.id === recipe.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900">{recipe.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {recipe.servings} {t('menu.servings')}
                        {' · '}
                        {Math.round(recipe.perServing.energyKcal)} kcal/portion
                        {' · '}
                        P {Math.round(recipe.perServing.proteins)}g
                        {' · '}
                        G {Math.round(recipe.perServing.fat)}g
                      </div>
                    </button>
                  ))}
                  {recipesData?.recipes.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">{t('menu.noRecipesFound')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Semi/cheat tier: label */}
          {(tier === 'semi' || tier === 'cheat') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('menu.mealLabel')}</label>
              <input
                type="text"
                placeholder={t('menu.mealLabelPlaceholder')}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Semi tier: optional nutrition */}
          {tier === 'semi' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('menu.estimatedNutrition')}</label>
              <div className="grid grid-cols-2 gap-3">
                <NutritionInput label="kcal" value={energyKcal} onChange={setEnergyKcal} />
                <NutritionInput label={t('menu.proteins')} value={proteins} onChange={setProteins} />
                <NutritionInput label={t('menu.carbs')} value={carbohydrates} onChange={setCarbohydrates} />
                <NutritionInput label={t('menu.fat')} value={fat} onChange={setFat} />
                <NutritionInput label={t('menu.fiber')} value={fiber} onChange={setFiber} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {t('app.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('menu.addToMenu')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NutritionInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="number"
        min="0"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
      />
    </div>
  );
}
